import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer, Box, Tooltip, IconButton, Collapse, Dialog, DialogActions,
} from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LockResetIcon from "@mui/icons-material/LockReset";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GavelIcon from "@mui/icons-material/Gavel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ChatIcon from "@mui/icons-material/Chat";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CallIcon from "@mui/icons-material/Call";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../redux/reducers/userReducer";
import { changeThemeColor } from "../redux/reducers/themeValueReducer";
import GradientButton from "../components/common/GradiantButton";
import SecondaryButton from "../components/common/SecondaryButton";
import { CONTACT_PHONES } from "../constants/contactPhones";

/* ── Font injection ── */
const injectFont = () => {
  if (typeof document === "undefined" || document.getElementById("sb-font")) return;
  const l = document.createElement("link");
  l.id = "sb-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
};
injectFont();

/* ── Design tokens ── */
const T = {
  accent: "#4F46E5",
  accentLight: "#EEF2FF",
  accentMid: "#C7D2FE",
  danger: "#EF4444",
  dangerLight: "#FEF2F2",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0",
  bg: "#FFFFFF",
  bgSub: "#F8FAFC",
  font: "'Sora', sans-serif",
};

/* ── Active route detection ── */
const useActive = (path) => useLocation().pathname === path;

/* ════════════════════ NAV ITEM ════════════════════ */
const NavItem = ({ item, showText, onNavigate, depth = 0 }) => {
  const location = useLocation();
  const [open, setOpen] = useState(
    item.children?.some((c) => location.pathname === c.path) ?? false
  );
  const isActive = location.pathname === item.path;
  const hasChildren = !!item.children;

  const handleClick = () => {
    if (hasChildren) { setOpen((p) => !p); return; }
    onNavigate(item.path);
  };

  return (
    <>
      <Tooltip title={!showText ? item.text : ""} placement="right" arrow>
        <Box
          onClick={handleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: showText ? 1.5 : 0,
            justifyContent: showText ? "flex-start" : "center",
            px: showText ? (depth > 0 ? 2.5 : 2) : 1.5,
            py: 1.1,
            mx: 1,
            mb: 0.4,
            pl: depth > 0 && showText ? 4.5 : undefined,
            borderRadius: "12px",
            cursor: "pointer",
            fontFamily: T.font,
            fontSize: depth > 0 ? "0.82rem" : "0.88rem",
            fontWeight: isActive ? 600 : 500,
            color: isActive ? T.accent : T.textMuted,
            background: isActive ? T.accentLight : "transparent",
            transition: "all 0.15s ease",
            userSelect: "none",
            "&:hover": {
              background: isActive ? T.accentLight : T.bgSub,
              color: T.accent,
              "& .sb-icon": { color: T.accent },
            },
          }}
        >
          {/* Icon */}
          <Box
            className="sb-icon"
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "9px",
              background: isActive ? T.accentMid : "transparent",
              color: isActive ? T.accent : T.textMuted,
              transition: "all 0.15s",
              "& svg": { fontSize: "1.15rem" },
            }}
          >
            {item.icon}
          </Box>

          {/* Label */}
          {showText && (
            <Box
              sx={{
                flex: 1,
                whiteSpace: "normal",
                lineHeight: 1.2,
                fontSize: depth > 0 ? "0.8rem" : "0.88rem"
              }}
            >
              {item.text}
            </Box>
          )}

          {/* Expand chevron */}
          {showText && hasChildren && (
            <Box sx={{ color: T.textMuted, display: "flex", "& svg": { fontSize: "1rem" } }}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          )}
        </Box>
      </Tooltip>

      {/* Children */}
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            sx={{
              ml: showText ? 2.5 : 0,
              pl: showText ? 1 : 0,
              borderLeft: showText ? `2px solid ${T.accentMid}` : "none",
              mb: 0.5,
            }}
          >
            {item.children.map((child) => (
              <NavItem key={child.text} item={child} showText={showText} onNavigate={onNavigate} depth={1} />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
};

/* ════════════════════ SECTION LABEL ════════════════════ */
const SectionLabel = ({ label, showText }) =>
  showText ? (
    <Box sx={{
      px: 3, pt: 2, pb: 0.5,
      fontFamily: T.font, fontSize: "0.68rem", fontWeight: 700,
      letterSpacing: "0.12em", textTransform: "uppercase", color: "#94A3B8",
    }}>
      {label}
    </Box>
  ) : (
    <Box sx={{ my: 1, mx: "auto", width: 20, height: 1, background: T.border }} />
  );

/* ════════════════════ SIDEBAR CONTENT ════════════════════ */
const SidebarContent = ({ onDrawerToggle, showText, collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useSelector((d) => d.themeValue.colors);
  const themeMode = useSelector((s) => s.themeValue.theme);

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const mainMenu = [
    ...(isMobile ? [{ text: "Post Property", icon: <AddBoxIcon />, path: "/post-your-property" }] : []),
    {
      text: "My Properties",
      icon: <HomeWorkIcon />,
      children: [
        { text: "PropertyList" , icon:<HomeWorkIcon /> , path: "/profile/properties"},
        { text: "Customer Enquiries", icon: <ChatIcon />, path: "/profile/enquiries" },
        { text: "Site Visit Requests", icon: <EventAvailableIcon />, path: "/profile/sitevisits" },
      ],
    },
    { text: "Subscription Plans", icon: <WorkspacePremiumIcon />, path: "/owners/subscription/plans" },
    { text: "Booked Properties", icon: <HomeWorkIcon />, path: "/bookedproperties" },
    { text: "Wallet", icon: <AccountBalanceWalletIcon />, path: "/Wallet" },
  ];

  const settingsMenu = [
    { text: "Bank Details", icon: <AccountBalanceIcon />, path: "/BankDetails" },
    { text: "Change Password", icon: <LockResetIcon />, path: "/ChangePassword" },
    { text: "Legal Team", icon: <GavelIcon />, path: "/legal-team" },
    { text: "Help", icon: <HelpOutlineIcon />, path: "/Help" },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onDrawerToggle) onDrawerToggle();
  };

  const handleConfirmLogout = () => {
    dispatch(logoutUser());
    navigate("/");
    setOpenLogoutDialog(false);
  };

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      height: "100%", background: T.bg, fontFamily: T.font,
    }}>

      {/* ── Pin / Unpin ── */}
      {!isMobile && setCollapsed && (
        <Box sx={{
          display: "flex",
          justifyContent: showText ? "flex-end" : "center",
          px: 1.5, pt: 1.5, pb: 1,
          borderBottom: `1px solid ${T.border}`,
        }}>
          <Tooltip title={collapsed ? "Pin sidebar" : "Unpin sidebar"} placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                color: T.accent, borderRadius: "8px",
                "&:hover": { background: T.accentLight },
              }}
            >
              {collapsed ? <PushPinOutlinedIcon fontSize="small" /> : <PushPinIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* ── Scrollable nav ── */}
      <Box sx={{
        flex: 1, overflowY: "auto", overflowX: "hidden",
        py: 1.5,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { background: T.border, borderRadius: 2 },
      }}>
        <SectionLabel label="Menu" showText={showText} />
        {mainMenu.map((item) => (
          <NavItem key={item.text} item={item} showText={showText} onNavigate={handleNavigation} />
        ))}

        <SectionLabel label="Settings" showText={showText} />
        {settingsMenu.map((item) => (
          <NavItem key={item.text} item={item} showText={showText} onNavigate={handleNavigation} />
        ))}

        {/* Mobile call section */}
        {isMobile && showText && (
          <Box sx={{
            mx: 2, mt: 2, p: 2,
            background: T.accentLight,
            borderRadius: "14px",
            border: `1px solid ${T.accentMid}`,
          }}>
            <Box sx={{ fontFamily: T.font, fontWeight: 700, fontSize: "0.8rem", color: T.accent, mb: 0.5 }}>
              Call Us
            </Box>
            <Box sx={{ fontFamily: T.font, fontSize: "0.72rem", color: T.textMuted, mb: 1.5 }}>
              Mon – Sun · 9 AM – 7 PM
            </Box>
            {CONTACT_PHONES.map((num) => (
              <Box key={num} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <IconButton
                  component="a"
                  href={`tel:${num.replace(/\s/g, "")}`}
                  size="small"
                  sx={{
                    background: T.accent, color: "#fff", borderRadius: "9px", p: 0.6,
                    "&:hover": { background: "#3730A3" }, "& svg": { fontSize: "0.9rem" }
                  }}
                >
                  <CallIcon />
                </IconButton>
                <Box sx={{ fontFamily: T.font, fontWeight: 600, fontSize: "0.82rem", color: T.text }}>
                  {num}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Logout ── */}
      <Box sx={{ borderTop: `1px solid ${T.border}`, p: 1 }}>
        <Tooltip title={!showText ? "Log Out" : ""} placement="right" arrow>
          <Box
            onClick={() => setOpenLogoutDialog(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: showText ? 1.5 : 0,
              justifyContent: showText ? "flex-start" : "center",
              px: showText ? 2 : 1.5,
              py: 1.1,
              borderRadius: "12px",
              cursor: "pointer",
              fontFamily: T.font,
              fontSize: "0.88rem",
              fontWeight: 500,
              color: "#EF4444",
              transition: "all 0.15s",
              "&:hover": {
                background: T.dangerLight,
                "& .logout-icon": { background: "#FEE2E2" },
              },
            }}
          >
            <Box
              className="logout-icon"
              sx={{
                flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                width: 32, height: 32, borderRadius: "9px",
                color: "#EF4444", transition: "background 0.15s",
                "& svg": { fontSize: "1.15rem" },
              }}
            >
              <LogoutIcon />
            </Box>
            {showText && "Log Out"}
          </Box>
        </Tooltip>
      </Box>

      {/* ── Logout dialog ── */}
      <Dialog
        open={openLogoutDialog}
        onClose={() => setOpenLogoutDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
            p: 1, minWidth: 320,
            fontFamily: T.font,
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          {/* Icon circle */}
          <Box sx={{
            width: 64, height: 64, borderRadius: "50%",
            background: T.dangerLight, display: "flex",
            alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 2,
          }}>
            <LogoutIcon sx={{ fontSize: 30, color: "#EF4444" }} />
          </Box>
          <Box sx={{ fontFamily: T.font, fontWeight: 700, fontSize: "1.15rem", color: T.text, mb: 1 }}>
            Log out?
          </Box>
          <Box sx={{ fontFamily: T.font, fontSize: "0.85rem", color: T.textMuted, lineHeight: 1.6 }}>
            You'll need to sign in again to access your account.
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5, justifyContent: "center" }}>
          <SecondaryButton text="Cancel" onClick={() => setOpenLogoutDialog(false)} />
          <GradientButton text="Log Out" onClick={handleConfirmLogout} />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ════════════════════ SIDEBAR WRAPPER ════════════════════ */
const Sidebar = ({ collapsed, setCollapsed, drawerWidth, mobileOpen, onDrawerToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const effectiveWidth = isHovered && collapsed ? 240 : drawerWidth;
  const showText = !collapsed || (collapsed && isHovered);

  return (
    <>
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth, borderRadius: "0 16px 16px 0",
            boxShadow: "4px 0 24px rgba(0,0,0,0.08)",
          },
        }}
      >
        <SidebarContent onDrawerToggle={onDrawerToggle} showText={true} />
      </Drawer>

      {/* Desktop */}
      <Box
        sx={{ display: { xs: "none", sm: "block" } }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Drawer
          variant="permanent"
          sx={{
            "& .MuiDrawer-paper": {
              width: effectiveWidth,
              transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)",
              boxSizing: "border-box",
              height: "calc(100vh - 78px)",
              top: "78px",
              position: "fixed",
              overflowY: "auto",
              overflowX: "hidden",
              borderRight: `1px solid ${T.border}`,
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
              boxShadow: "4px 0 24px rgba(0,0,0,0.04)",
            },
          }}
          open
        >
          <SidebarContent
            showText={showText}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            onDrawerToggle={onDrawerToggle}
          />
        </Drawer>
      </Box>
    </>
  );
};

export default Sidebar;
